import { useEffect, useState } from "react";
import { useYearContext } from "../../hooks/useYearContext";
import { Link } from "react-router-dom";
import Loader from "../../components/Loader";
import { Game, Team, Group, GroupCode } from "../../types";

interface TeamStats {
    z: number;
    v: number;
    p: number;
    r: number;
    g: number;
    ig: number;
    b: number;
    WinAgainst: number[];
}

interface TeamWithStats extends Team {
    statsByCode?: { [codeId: number]: TeamStats };
    z?: number;
    v?: number;
    p?: number;
    r?: number;
    g?: number;
    ig?: number;
    b?: number;
    WinAgainst?: number[];
}

const UserLeaderboard = () => {
    const { current } = useYearContext();
    const [, setGames] = useState<Game[]>([]);
    const [teams, setTeams] = useState<TeamWithStats[]>([]);
    const [groups, setGroups] = useState<Group[]>([]);
    const [codes, setCodes] = useState<GroupCode[]>([]);

    useEffect(() => {
        if (current) {
            let tempGames: Game[];
            let tempCodes: GroupCode[];
            
            const getGames = async () => {
                const response = await fetch(`${process.env.REACT_APP_PATH}/api/game/finished?year=${current.year}`);
                const json: Game[] = await response.json();
                if (response.ok) {
                    setGames(json);
                    tempGames = json;
                    getTeams();
                } else {
                    alert((json as any).error);
                }
            };

            const getGroups = async () => {
                const response = await fetch(`${process.env.REACT_APP_PATH}/api/group?year=${current.year}`, {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });
                const json: Group[] = await response.json();
                if (response.ok) {
                    setGroups(json);
                } else {
                    alert((json as any).error);
                }
            };

            const getGroupCodes = async () => {
                const response = await fetch(`${process.env.REACT_APP_PATH}/api/group/codes?year=${current.year}`, {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });
                const json: GroupCode[] = await response.json();
                if (response.ok) {
                    console.log(json);
                    setCodes(json);
                    tempCodes = json;
                    getGames(); // Start fetching games after codes are available
                } else {
                    alert((json as any).error);
                }
            };

            const getTeams = async () => {
                const response = await fetch(`${process.env.REACT_APP_PATH}/api/teams?year=${current.year}`, {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });
                const json: Team[] = await response.json();
                if (response.ok) {
                    calculateTeams(json, tempGames, tempCodes);
                } else {
                    alert((json as any).error);
                }
            };

            getGroupCodes();
            getGroups();
        }
    }, [current]);

    const calculateTeams = (teams: Team[], games: Game[], groupCodes: GroupCode[]) => {
        // Initialize stats for each team - now using objects to store stats per group code
        const teamsWithStats: TeamWithStats[] = teams.map(e => ({
            ...e,
            statsByCode: {}
        }));

        // Process each finished group game
        games.filter(x => x.text === "group").forEach(game => {
            let ta = teamsWithStats.find(x => x.ID === game.team_a_id);
            let tb = teamsWithStats.find(x => x.ID === game.team_b_id);

            if (!ta || !tb) return; // Skip if teams not found

            // Get all group IDs that team A belongs to
            const taGroupIds = ta.group_codes 
                ? [...new Set(ta.group_codes.map(code => {
                    const gc = groupCodes.find(g => g.ID === code);
                    return gc ? gc.group_id : null;
                }).filter((id): id is number => id !== null))]
                : [];

            // Get all group IDs that team B belongs to
            const tbGroupIds = tb.group_codes 
                ? [...new Set(tb.group_codes.map(code => {
                    const gc = groupCodes.find(g => g.ID === code);
                    return gc ? gc.group_id : null;
                }).filter((id): id is number => id !== null))]
                : [];

            // Find all parent groups that BOTH teams share
            const sharedGroupIds = taGroupIds.filter(groupId => tbGroupIds.includes(groupId));

            // For each shared parent group, count this match
            sharedGroupIds.forEach(parentGroupId => {
                // Get all codes in this parent group
                const parentGroupCodeIds = groupCodes
                    .filter(gc => gc.group_id === parentGroupId)
                    .map(gc => gc.ID);

                // Find which codes from this parent group each team belongs to
                const taCodesInGroup = ta.group_codes 
                    ? ta.group_codes.filter(code => parentGroupCodeIds.includes(code))
                    : [];
                const tbCodesInGroup = tb.group_codes 
                    ? tb.group_codes.filter(code => parentGroupCodeIds.includes(code))
                    : [];

                // For team A, add stats under each of their codes in this group
                taCodesInGroup.forEach(taCode => {
                    if (!ta.statsByCode![taCode]) {
                        ta.statsByCode![taCode] = { z: 0, v: 0, p: 0, r: 0, g: 0, ig: 0, b: 0, WinAgainst: [] };
                    }

                    ta.statsByCode![taCode].z++;
                    ta.statsByCode![taCode].g += game.team_a_goals;
                    ta.statsByCode![taCode].ig += game.team_b_goals;

                    if (game.winner === ta.ID) {
                        ta.statsByCode![taCode].v++;
                        ta.statsByCode![taCode].b += 2;
                        ta.statsByCode![taCode].WinAgainst.push(tb.ID);
                    } else if (game.winner === tb.ID) {
                        ta.statsByCode![taCode].p++;
                    } else if (game.tie) {
                        ta.statsByCode![taCode].r++;
                        ta.statsByCode![taCode].b += 1;
                    }
                });

                // For team B, add stats under each of their codes in this group
                tbCodesInGroup.forEach(tbCode => {
                    if (!tb.statsByCode![tbCode]) {
                        tb.statsByCode![tbCode] = { z: 0, v: 0, p: 0, r: 0, g: 0, ig: 0, b: 0, WinAgainst: [] };
                    }

                    tb.statsByCode![tbCode].z++;
                    tb.statsByCode![tbCode].g += game.team_b_goals;
                    tb.statsByCode![tbCode].ig += game.team_a_goals;

                    if (game.winner === tb.ID) {
                        tb.statsByCode![tbCode].v++;
                        tb.statsByCode![tbCode].b += 2;
                        tb.statsByCode![tbCode].WinAgainst.push(ta.ID);
                    } else if (game.winner === ta.ID) {
                        tb.statsByCode![tbCode].p++;
                    } else if (game.tie) {
                        tb.statsByCode![tbCode].r++;
                        tb.statsByCode![tbCode].b += 1;
                    }
                });
            });
        });

        setTeams(teamsWithStats);
    };

    return (
        <div className="  w-[90vw] md:w-[70vw] xl:w-[46vw] mx-auto">
            <h1 className=" md:hidden text-accent text-center text-6xl  mt-12">Priebeh</h1>
            {(teams.length > 0 && codes.length > 0) && groups.map(e => {
                // Filter teams that have any code belonging to this group
                // and attach the stats for that specific code to each team
                const teamsInGroup = teams.filter(team => {
                    // Check if team has group_codes array
                    if (team.group_codes && Array.isArray(team.group_codes)) {
                        return team.group_codes.some(codeId => {
                            const code = codes.find(z => z.ID === codeId);
                            return code && code.name.includes(e.group_name);
                        });
                    }
                    // Fallback to old single group_code if array doesn't exist
                    else if (team.group_code) {
                        const code = codes.find(z => z.ID === team.group_code);
                        return code && code.name.includes(e.group_name);
                    }
                    return false;
                }).map(team => {
                    // Find ALL code IDs for this team in this group
                    let relevantCodeIds: number[] = [];
                    if (team.group_codes && Array.isArray(team.group_codes)) {
                        relevantCodeIds = team.group_codes.filter(codeId => {
                            const code = codes.find(z => z.ID === codeId);
                            return code && code.name.includes(e.group_name);
                        });
                    } else if (team.group_code) {
                        const code = codes.find(z => z.ID === team.group_code);
                        if (code && code.name.includes(e.group_name)) {
                            relevantCodeIds = [team.group_code];
                        }
                    }

                    // Aggregate stats from ALL relevant codes for this group
                    const stats: TeamStats = { z: 0, v: 0, p: 0, r: 0, g: 0, ig: 0, b: 0, WinAgainst: [] };
                    relevantCodeIds.forEach(codeId => {
                        const codeStats = team.statsByCode?.[codeId];
                        if (codeStats) {
                            stats.z += codeStats.z;
                            stats.v += codeStats.v;
                            stats.p += codeStats.p;
                            stats.r += codeStats.r;
                            stats.g += codeStats.g;
                            stats.ig += codeStats.ig;
                            stats.b += codeStats.b;
                            stats.WinAgainst = [...stats.WinAgainst, ...codeStats.WinAgainst];
                        }
                    });
                    
                    return {
                        ...team,
                        // Override with group-specific stats
                        z: stats.z,
                        v: stats.v,
                        p: stats.p,
                        r: stats.r,
                        g: stats.g,
                        ig: stats.ig,
                        b: stats.b,
                        WinAgainst: stats.WinAgainst
                    };
                }).sort((a, b) => (b.b || 0) - (a.b || 0) || ((b.g || 0) - (b.ig || 0)) - ((a.g || 0) - (a.ig || 0)) || (b.g || 0) - (a.g || 0));
                
                return (
                    <div key={e.ID}>
                        {teamsInGroup.length > 0 &&
                            <div className="my-12 bg-white p-8 border border-borders">
                                <h2 className="text-xl font-semibold mb-3">Skupina {e.group_name}</h2>
                                <div className="table w-full ">
                                    <div className="table-row  child:border-borders text-dark-text child:text-sm">
                                        <div className="hidden md:table-cell w-10"></div>
                                        <div className="table-cell">Tím</div>
                                        <div className="table-cell pr-3 md:px-3 w-24">Z</div>
                                        <div className="table-cell pr-3 md:px-3 w-24">V</div>
                                        <div className="table-cell pr-3 md:px-3 w-24">P</div>
                                        <div className="table-cell pr-3 md:px-3 w-24">R</div>
                                        <div className="hidden md:table-cell pr-3 md:px-3 w-24">G</div>
                                        <div className="hidden md:table-cell pr-3 md:px-3 w-24">IG</div>
                                        <div className="table-cell pr-3 md:px-3 w-24">+/-</div>
                                        <div className="table-cell pr-3 md:px-3 w-24">B</div>
                                    </div>
                                    {teamsInGroup.map((f, i) => (
                                        <Link to={`/teams/${f.ID}`} key={f.ID} className="table-row child:border-t child:border-borders  child:py-3">
                                            <div className="hidden md:table-cell w-[7%] text-center">{i + 1}</div>
                                            <div className="table-cell w-2/3">{f.name}</div>
                                            <div className="table-cell pr-3 md:px-3 w-24">{f.z}</div>
                                            <div className="table-cell pr-3 md:px-3 w-24">{f.v}</div>
                                            <div className="table-cell pr-3 md:px-3 w-24">{f.p}</div>
                                            <div className="table-cell pr-3 md:px-3 w-24">{f.r}</div>
                                            <div className="hidden md:table-cell pr-3 md:px-3 w-24">{f.g}</div>
                                            <div className="hidden md:table-cell pr-3 md:px-3 w-24">{f.ig}</div>
                                            <div className="table-cell pr-3 md:px-3 w-24">{(f.g || 0) - (f.ig || 0)}</div>
                                            <div className="table-cell pr-3 md:px-3 w-24">{f.b}</div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        }
                    </div>
                );
            })}
            {!(groups.length > 0 && teams.length > 0) && <Loader />}
        </div>
    );
};

export default UserLeaderboard;
